// VanirApp — Full app shell for vanir.seidr.makers.bot
// Copied from App.jsx as starting point. Modify freely — only affects vanir.
// All imports point to local vanir/ components or shared contexts.

import React, { useState, useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { TeamProvider, useTeam } from '../contexts/TeamContext';
import { PremiumProvider, useIsPremium } from '../contexts/PremiumContext';
import { RegionLockProvider } from '../contexts/RegionLockContext';
import { ProjectsProvider, useProjects } from '../contexts/ProjectsContext';
import { useOrg } from '../contexts/OrgContext';
import { MapErrorBoundary } from '../components/MapErrorBoundary';
import FeedbackWidget, { openFeedback } from '../components/FeedbackWidget';
import LoginRefusal, { RETRY_LOGIN } from '../components/LoginRefusal';
import ProjectsPage from '../components/ProjectsPage';
import ProjectDetail from '../components/ProjectDetail';
import VanirSiteScreener from './components/VanirSiteScreener';
import { useTranslation } from '../translations';
import '../App.css';
import './vanir.css';

const AUTH0_DOMAIN = import.meta.env?.VITE_AUTH0_DOMAIN || '';
const AUTH0_CLIENT_ID = import.meta.env?.VITE_AUTH0_CLIENT_ID || '';
const AUTH0_AUDIENCE = import.meta.env?.VITE_AUTH0_AUDIENCE || '';
const AUTH0_ORG_ID = import.meta.env?.VITE_AUTH0_ORG_ID || '';
const AUTH0_CONFIGURED = AUTH0_DOMAIN && !AUTH0_DOMAIN.includes('your-tenant');

// Login gate — requires authentication to access vanir/app
function LoginGate({ children }) {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const { branding } = useOrg();
  const companyName = branding?.company_name || 'Vanir';
  
  if (isLoading) {
    return (
      <div className="login-gate">
        <div className="login-gate-content">
          <div className="login-gate-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <div className="login-gate">
        <div className="login-gate-content">
          <h1 className="login-gate-logo">{companyName}</h1>
          <p className="login-gate-tagline">Site screening for renewable energy</p>
          <LoginRefusal error={error} orgName={companyName} />
          <button 
            className="login-gate-btn"
            onClick={() => loginWithRedirect(error ? RETRY_LOGIN : undefined)}
          >
            {error ? 'Sign in with another account' : 'Sign in to continue'}
          </button>
        </div>
      </div>
    );
  }
  
  return children;
}

function ProjectBadge() {
  const { projectCount, enrichingCount } = useProjects();
  if (!projectCount) return null;
  return (
    <span className={`nav-badge ${enrichingCount > 0 ? 'nav-badge-enriching' : ''}`}>
      {projectCount}{enrichingCount > 0 ? '...' : ''}
    </span>
  );
}

function AuthButton() {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user } = useAuth0();

  if (isLoading) return <span className="auth-loading">...</span>;

  if (isAuthenticated) {
    return (
      <div className="auth-user">
        <span className="auth-email">{user?.name || user?.email}</span>
        <button className="btn-slim btn-signout"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <button className="btn-slim btn-signin"
            onClick={() => loginWithRedirect()}>
      Sign In
    </button>
  );
}

function FallbackAuthButton() {
  const { team, currentUser, signIn, signOut } = useTeam();
  const [open, setOpen] = useState(false);

  if (currentUser) {
    return (
      <div className="team-pill" onClick={() => setOpen(o => !o)}>
        <span className="team-pill-avatar" style={{ background: currentUser.color }}>
          {currentUser.initials}
        </span>
        <span className="team-pill-name">{currentUser.name}</span>
        <span className="team-pill-caret">▾</span>
        {open && (
          <div className="team-menu" onClick={e => e.stopPropagation()}>
            <div className="team-menu-section">Switch user</div>
            {team.filter(u => u.id !== currentUser.id).map(u => (
              <button key={u.id} className="team-menu-item"
                      onClick={() => { signIn(u.id); setOpen(false); }}>
                <span className="team-pill-avatar" style={{ background: u.color }}>{u.initials}</span>
                {u.name}
              </button>
            ))}
            <div className="team-menu-divider" />
            <button className="team-menu-item team-menu-signout"
                    onClick={() => { signOut(); setOpen(false); }}>
              Sign out
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="team-pill" onClick={() => setOpen(o => !o)}>
      <span className="team-pill-name">Sign in</span>
      <span className="team-pill-caret">▾</span>
      {open && (
        <div className="team-menu" onClick={e => e.stopPropagation()}>
          <div className="team-menu-section">Pick a team member</div>
          {team.map(u => (
            <button key={u.id} className="team-menu-item"
                    onClick={() => { signIn(u.id); setOpen(false); }}>
              <span className="team-pill-avatar" style={{ background: u.color }}>{u.initials}</span>
              {u.name}
              <span className="team-menu-email">{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VanirContent() {
  const { branding } = useOrg();
  const language = 'en';
  const t = useTranslation(language);

  const [route, setRoute] = useState(() => window.location.hash);

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Route matching — all routes stay within vanir subdomain
  const isProjects = route === '#/projects' || route.startsWith('#/projects?');
  const projectMatch = route.match(/^#\/projects\/(.+)$/);
  const isProjectDetail = !!projectMatch;
  const isMap = !isProjects && !isProjectDetail;
  const needsScroll = isProjects || isProjectDetail;

  // Use org branding for header text
  const companyName = branding?.company_name || 'Vanir';

  return (
    <TeamProvider>
    <PremiumProvider>
    <RegionLockProvider>
    <ProjectsProvider>
      <div className="App vanir-app">
        {/* ── Vanir Header ──────────────────────────────────────── */}
        <header className="header-top vanir-header">
          <div className="header-logo" onClick={() => { window.location.hash = ''; }}>
            <span className="header-logo-text vanir-logo">{companyName}</span>
          </div>
          <nav className="header-nav">
            <a href="#/" className={`nav-link ${isMap ? 'nav-active' : ''}`}>Map</a>
            <a href="#/projects" className={`nav-link ${isProjects || isProjectDetail ? 'nav-active' : ''}`}>
              Projects <ProjectBadge />
            </a>
            {!isMap && (
              <button type="button" className="nav-link nav-feedback" onClick={openFeedback}>
                💬 Feedback
              </button>
            )}
          </nav>
          <div className="header-right">
            {AUTH0_CONFIGURED ? <AuthButton /> : <FallbackAuthButton />}
          </div>
        </header>

        {/* ── Main content ─────────────────────────────────────── */}
        <main className="app-main" style={needsScroll ? { overflow: 'auto' } : undefined}>
          {isProjectDetail && (
            <ProjectDetail projectId={projectMatch[1]} />
          )}
          <div style={{ display: isProjects ? undefined : 'none', overflow: 'auto', height: '100%' }}>
            <ProjectsPage />
          </div>
          <div style={{ display: isMap ? undefined : 'none', height: '100%' }}>
            <MapErrorBoundary>
              <VanirSiteScreener language={language} />
            </MapErrorBoundary>
          </div>
        </main>
        <FeedbackWidget showFab={false} />
      </div>
    </ProjectsProvider>
    </RegionLockProvider>
    </PremiumProvider>
    </TeamProvider>
  );
}

export default function VanirApp() {
  if (AUTH0_CONFIGURED) {
    return (
      <Auth0Provider
        domain={AUTH0_DOMAIN}
        clientId={AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/app`,
          audience: AUTH0_AUDIENCE || undefined,
          organization: AUTH0_ORG_ID || undefined,
        }}
        cacheLocation="memory"
      >
        <LoginGate>
          <VanirContent />
        </LoginGate>
      </Auth0Provider>
    );
  }
  // Dev mode without Auth0 — no login gate
  return <VanirContent />;
}
